package link.erpango.quip.ui

import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.Card
import androidx.compose.material.Divider
import androidx.compose.material.Icon
import androidx.compose.material.IconButton
import androidx.compose.material.MaterialTheme
import androidx.compose.material.Scaffold
import androidx.compose.material.Text
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.ArrowBack
import androidx.compose.material.icons.filled.Check
import androidx.compose.material.icons.filled.Edit
import androidx.compose.material.icons.filled.Info
import androidx.compose.material.icons.filled.List
import androidx.compose.material.icons.filled.Person
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import compose.icons.FontAwesomeIcons
import compose.icons.fontawesomeicons.Solid
import compose.icons.fontawesomeicons.solid.CashRegister
import compose.icons.fontawesomeicons.solid.File
import compose.icons.fontawesomeicons.solid.List
import compose.icons.fontawesomeicons.solid.Road
import compose.icons.fontawesomeicons.solid.Warehouse
import link.erpango.quip.domain.entities.DMaintenance
import link.erpango.quip.domain.entities.DSparePart
import link.erpango.quip.theme.QuipColors
import link.erpango.quip.ui.components.QuipCleanInfoRow
import link.erpango.quip.ui.components.QuipDialogGeneric
import link.erpango.quip.ui.components.QuipQuickActionButton
import link.erpango.quip.ui.components.QuipSectionHeader
import link.erpango.quip.ui.components.QuipTransparentHeader
import org.jetbrains.compose.resources.stringResource
import quip.composeapp.generated.resources.*

@Composable
fun MaintenanceDetailScreen(
    maintenance: DMaintenance,
    onEditClick: () -> Unit,
    onSignClick: () -> Unit,
    onBackClick: () -> Unit,
    onSparePartClick: (DSparePart) -> Unit,
    onDocumentsClick: () -> Unit,
) {
    var showSignDialog by remember { mutableStateOf(false) }
    
    // Sign confirmation dialog
    if (showSignDialog) {
        QuipDialogGeneric(
            title = stringResource(Res.string.sign_maintenance_title),
            message = stringResource(Res.string.sign_maintenance_message),
            confirmButtonText = stringResource(Res.string.sign_maintenance_confirm),
            dismissButtonText = stringResource(Res.string.cancel),
            onConfirm = {
                showSignDialog = false
                onSignClick()
            },
            onDismiss = { showSignDialog = false },
            isConfirmColored = true
        )
    }
    
    Scaffold(
        backgroundColor = QuipColors.colorBackground
    ) { paddingValues ->
        Column(modifier = Modifier.fillMaxSize().padding(paddingValues)) {
            
            // 1. Header
            QuipTransparentHeader {
                Row(verticalAlignment = Alignment.CenterVertically) {
                    IconButton(onClick = onBackClick) {
                        Icon(
                            Icons.Default.ArrowBack,
                            contentDescription = "Back",
                            tint = QuipColors.colorText
                        )
                    }
                    Spacer(modifier = Modifier.width(8.dp))
                    Text(
                        text = stringResource(Res.string.maintenance_details),
                        style = MaterialTheme.typography.h6,
                        color = QuipColors.colorText
                    )
                }
            }

            // 2. Content
            LazyColumn(
                modifier = Modifier
                    .fillMaxSize()
                    .weight(1f),
                contentPadding = PaddingValues(16.dp),
                verticalArrangement = Arrangement.spacedBy(16.dp)
            ) {
                
                // Actions
                item {
                    Row(
                        modifier = Modifier.fillMaxWidth().padding(bottom = 8.dp),
                        horizontalArrangement = Arrangement.SpaceEvenly
                    ) {
                        if (!maintenance.isSigned) {
                            QuipQuickActionButton(
                                icon = Icons.Default.Edit,
                                label = stringResource(Res.string.edit_maintenance),
                                onClick = onEditClick
                            )
                            QuipQuickActionButton(
                                icon = Icons.Default.Check,
                                label = stringResource(Res.string.sign_maintenance),
                                onClick = { showSignDialog = true }
                            )
                        }
                        QuipQuickActionButton(
                            icon = FontAwesomeIcons.Solid.File,
                            label = "Docs",
                            onClick = onDocumentsClick
                        )
                    }
                }

                // Info Card
                item {
                    Column {
                        QuipSectionHeader(
                            title = stringResource(Res.string.maintenance_details),
                            icon = Icons.Default.Info
                        )
                        
                        Card(
                            elevation = 0.dp,
                            backgroundColor = Color.White,
                            shape = RoundedCornerShape(12.dp),
                            border = androidx.compose.foundation.BorderStroke(1.dp, QuipColors.colorBackground),
                            modifier = Modifier.fillMaxWidth()
                        ) {
                            Column(modifier = Modifier.padding(16.dp)) {
                                QuipCleanInfoRow(
                                    label = stringResource(Res.string.odometer),
                                    value = "${maintenance.kilometers} km",
                                    icon = FontAwesomeIcons.Solid.Road
                                )
                                QuipCleanInfoRow(
                                    label = stringResource(Res.string.maintenance_cost),
                                    value = "${maintenance.cost}€",
                                    icon = FontAwesomeIcons.Solid.CashRegister
                                )
                                QuipCleanInfoRow(
                                    label = stringResource(Res.string.mechanic),
                                    value = maintenance.mechanic,
                                    icon = Icons.Default.Person
                                )
                                QuipCleanInfoRow(
                                    label = stringResource(Res.string.workshop),
                                    value = maintenance.workshop,
                                    icon = FontAwesomeIcons.Solid.Warehouse
                                )
                                if (maintenance.description.isNotBlank()) {
                                    Spacer(modifier = Modifier.height(12.dp))
                                    Text(
                                        text = stringResource(Res.string.description),
                                        style = MaterialTheme.typography.subtitle2,
                                        fontWeight = FontWeight.Bold,
                                        color = QuipColors.colorText
                                    )
                                    Spacer(modifier = Modifier.height(4.dp))
                                    Text(
                                        text = maintenance.description,
                                        style = MaterialTheme.typography.body2,
                                        color = QuipColors.colorTextSecondary
                                    )
                                }
                            }
                        }
                    }
                }

                // Spare Parts
                item {
                    QuipSectionHeader(
                        title = stringResource(Res.string.spare_parts),
                        icon = FontAwesomeIcons.Solid.List
                    )
                }

                if (maintenance.spareParts.isEmpty()) {
                    item {
                        Card(
                            elevation = 0.dp,
                            backgroundColor = Color.White,
                            shape = RoundedCornerShape(12.dp),
                            modifier = Modifier.fillMaxWidth().padding(vertical = 8.dp)
                        ) {
                            Text(
                                text = stringResource(Res.string.no_spare_parts_registered),
                                style = MaterialTheme.typography.body2,
                                color = QuipColors.colorText.copy(alpha = 0.6f),
                                modifier = Modifier.padding(16.dp),
                                textAlign = androidx.compose.ui.text.style.TextAlign.Center
                            )
                        }
                    }
                } else {
                    items(maintenance.spareParts) { sparePart ->
                        Box(modifier = Modifier.clickable { onSparePartClick(sparePart) }) {
                            SparePartItem(sparePart = sparePart)
                        }
                    }
                }
            }
        }
    }
}

@Composable
fun SparePartItem(sparePart: DSparePart) {
    Card(
        elevation = 0.dp,
        backgroundColor = Color.White,
        shape = RoundedCornerShape(12.dp),
        border = androidx.compose.foundation.BorderStroke(1.dp, QuipColors.colorBackground),
        modifier = Modifier.fillMaxWidth().padding(vertical = 4.dp)
    ) {
        Column(modifier = Modifier.padding(16.dp)) {
            Text(
                text = "${sparePart.brand} ${sparePart.model}",
                style = MaterialTheme.typography.subtitle1,
                fontWeight = FontWeight.Bold,
                color = QuipColors.colorPrimary
            )
            Spacer(modifier = Modifier.height(8.dp))
            
            QuipCleanInfoRow(
                label = stringResource(Res.string.spare_part_category),
                value = getSparePartCategoryString(sparePart.categoryId)
            )
            QuipCleanInfoRow(
                label = stringResource(Res.string.spare_part_type),
                value = getSparePartTypeString(sparePart.sparePartId)
            )
            
            if (sparePart.quantity != null && sparePart.unit != null) {
                QuipCleanInfoRow(
                    label = stringResource(Res.string.spare_part_quantity),
                    value = "${sparePart.quantity} ${sparePart.unit}"
                )
            }
            
            if (sparePart.durabilityDays != null) {
                 QuipCleanInfoRow(
                    label = stringResource(Res.string.spare_part_durability),
                    value = stringResource(Res.string.spare_part_durability_days).replace("%s", sparePart.durabilityDays.toString())
                )
            }

            if (sparePart.additionalInfo.isNotBlank()) {
                Spacer(modifier = Modifier.height(8.dp))
                Text(
                    text = stringResource(Res.string.spare_part_info).replace("%s", sparePart.additionalInfo),
                    style = MaterialTheme.typography.caption,
                    color = QuipColors.colorTextSecondary,
                    fontStyle = androidx.compose.ui.text.font.FontStyle.Italic
                )
            }
        }
    }
}